import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, FileText, AlertCircle, GraduationCap, ArrowUpDown, FileDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchablePicklist } from "@/components/ui/searchable-picklist";
import api from "@/services/api";

const ApplicationDownloadModal = lazy(() => import("./ApplicationDownloadModal"));

interface Institution {
    id: number;
    name: string;
}

interface TermOption {
    id: string;
    name: string;
}

interface ApplicationRow {
    application_number: string;
    applicant_name: string;
    applied_program_name: string;
    stage: string;
    applied_term_name: string;
    status: string;
    institution_id: number;
    institution_name: string;
    member_id: number | null;
}

const STAGE_OPTIONS = ["Evaluation", "Offer", "Accepted", "Enrolled", "Rejected", "Withdrawn"];
const STATUS_OPTIONS = ["Active", "Incomplete", "Submitted", "Complete"];
const SORT_OPTIONS = [
    { value: "__none__", label: "Default" },
    { value: "name_asc", label: "Applicant Name (A-Z)" },
    { value: "name_desc", label: "Applicant Name (Z-A)" },
];

function StageBadge({ stage }: { stage: string }) {
    const colorMap: Record<string, string> = {
        Evaluation: "border-blue-100 text-blue-600 bg-blue-50/50",
        Offer: "border-violet-100 text-violet-600 bg-violet-50/50",
        Accepted: "border-emerald-100 text-emerald-600 bg-emerald-50/50",
        Enrolled: "border-emerald-200 text-emerald-700 bg-emerald-50",
        Rejected: "border-red-100 text-red-600 bg-red-50/50",
        Withdrawn: "border-slate-200 text-slate-500 bg-slate-50",
    };
    return (
        <Badge variant="outline" className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest border whitespace-nowrap ${colorMap[stage] || "border-slate-200 text-slate-500 bg-slate-50"}`}>
            {stage || "—"}
        </Badge>
    );
}

export default function AgencyApplicationsTab() {
    const navigate = useNavigate();
    const [agencyStatus, setAgencyStatus] = useState<string | null>(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [institutions, setInstitutions] = useState<Institution[]>([]);
    const [terms, setTerms] = useState<TermOption[]>([]);
    const [countries, setCountries] = useState<string[]>([]);
    const [selectedInstitution, setSelectedInstitution] = useState<string>("__all__");
    const [searchNumber, setSearchNumber] = useState("");
    const [searchProgram, setSearchProgram] = useState("");
    const [searchApplicant, setSearchApplicant] = useState("");
    const [stageFilter, setStageFilter] = useState("__all__");
    const [statusFilter, setStatusFilter] = useState("__all__");
    const [termFilter, setTermFilter] = useState("");
    const [countryFilter, setCountryFilter] = useState("");
    const [sortOrder, setSortOrder] = useState("__none__");
    const [applications, setApplications] = useState<ApplicationRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);

    useEffect(() => {
        api.get("/agency/status/")
            .then((res) => {
                const s = res.data.status;
                setAgencyStatus(s);
                if (s === "Agreed") {
                    fetchApplications({});
                }
            })
            .catch(() => setAgencyStatus("Passive"))
            .finally(() => setStatusLoading(false));

        api.get("/agency/institutions/")
            .then((res) => setInstitutions(res.data.institutions || []))
            .catch(() => {});

        api.get("/integrations/hub/terms/")
            .then((res) => {
                if (res.data.status === "success") setTerms(res.data.terms || []);
            })
            .catch(() => {});

        api.get("/integrations/hub/countries/")
            .then((res) => {
                if (res.data.status === "success") setCountries(res.data.countries || []);
            })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchApplications = (overrides: Record<string, string>) => {
        setLoading(true);
        const params: Record<string, string> = {};
        const inst = overrides.institution ?? selectedInstitution;
        const num = overrides.search_number ?? searchNumber;
        const prog = overrides.search_program ?? searchProgram;
        const appl = overrides.search_applicant ?? searchApplicant;
        const st = overrides.stage ?? stageFilter;
        const appSt = overrides.status ?? statusFilter;
        const term = overrides.term ?? termFilter;
        const country = overrides.country ?? countryFilter;

        if (inst && inst !== "__all__") params.institution = inst;
        if (num.trim()) params.search_number = num.trim();
        if (prog.trim()) params.search_program = prog.trim();
        if (appl.trim()) params.search_applicant = appl.trim();
        if (st && st !== "__all__") params.stage = st;
        if (appSt && appSt !== "__all__") params.status = appSt;
        if (term.trim()) params.term = term.trim();
        if (country.trim()) params.country = country.trim();

        api.get("/agency/applications/", { params })
            .then((res) => setApplications(res.data.applications || []))
            .catch(() => setApplications([]))
            .finally(() => setLoading(false));
    };

    const handleSearch = () => fetchApplications({});

    const sortedApplications = useMemo(() => {
        if (sortOrder === "name_asc") {
            return [...applications].sort((a, b) => a.applicant_name.localeCompare(b.applicant_name));
        }
        if (sortOrder === "name_desc") {
            return [...applications].sort((a, b) => b.applicant_name.localeCompare(a.applicant_name));
        }
        return applications;
    }, [applications, sortOrder]);

    const termNames = useMemo(() => terms.map((term) => term.name), [terms]);

    if (statusLoading) {
        return (
            <div className="flex flex-col gap-4">
                {[1, 2, 3].map((i) => (
                    <Card key={i} className="h-16 animate-pulse rounded-2xl border border-slate-100" />
                ))}
            </div>
        );
    }

    if (agencyStatus !== "Agreed") {
        return (
            <div className="flex items-center justify-center min-h-[320px]">
                <Card className="border border-slate-100 shadow-sm rounded-2xl bg-white p-10 flex flex-col items-center gap-4 text-center max-w-md">
                    <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center">
                        <AlertCircle className="h-7 w-7 text-amber-500" />
                    </div>
                    <p className="text-base font-bold text-slate-800">Your agreement is not active yet.</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
                <h3 className="text-xl font-serif font-black text-slate-900 tracking-tight">My Applications</h3>
            </div>

            {showDownloadModal && (
                <Suspense fallback={null}>
                    <ApplicationDownloadModal
                        applications={sortedApplications}
                        onClose={() => setShowDownloadModal(false)}
                    />
                </Suspense>
            )}

            <Card className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4 space-y-3">
                {/* Row 1: Application Number, Program Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search by Application Number"
                            value={searchNumber}
                            onChange={(e) => setSearchNumber(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            className="pl-9 h-9 rounded-xl border-slate-200 text-sm"
                        />
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search Program Name"
                            value={searchProgram}
                            onChange={(e) => setSearchProgram(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            className="pl-9 h-9 rounded-xl border-slate-200 text-sm"
                        />
                    </div>
                </div>

                {/* Row 2: Applicant, Institution */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search by Applicant"
                            value={searchApplicant}
                            onChange={(e) => setSearchApplicant(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            className="pl-9 h-9 rounded-xl border-slate-200 text-sm"
                        />
                    </div>
                    {institutions.length > 0 ? (
                        <Select value={selectedInstitution} onValueChange={(v) => setSelectedInstitution(v)}>
                            <SelectTrigger className="h-9 rounded-xl border-slate-200 text-sm w-full">
                                <SelectValue placeholder="All Institutions" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All Institutions</SelectItem>
                                {institutions.map((inst) => (
                                    <SelectItem key={inst.id} value={String(inst.id)}>
                                        {inst.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <div />
                    )}
                </div>

                {/* Row 3: Stage, Status */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Select value={stageFilter} onValueChange={setStageFilter}>
                        <SelectTrigger className="h-9 rounded-xl border-slate-200 text-sm w-full">
                            <SelectValue placeholder="All Stages" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All Stages</SelectItem>
                            {STAGE_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-9 rounded-xl border-slate-200 text-sm w-full">
                            <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All Statuses</SelectItem>
                            {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Row 4: Term (picklist), Country (picklist) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <SearchablePicklist
                        options={termNames}
                        value={termFilter}
                        onChange={(val) => setTermFilter(val)}
                        placeholder="Select Term"
                        searchPlaceholder="Search terms..."
                        className="border-slate-200 h-9 text-sm"
                    />
                    <SearchablePicklist
                        options={countries}
                        value={countryFilter}
                        onChange={(val) => setCountryFilter(val)}
                        placeholder="Select Country"
                        searchPlaceholder="Search countries..."
                        className="border-slate-200 h-9 text-sm"
                    />
                </div>

                {/* Row 5: Sort, Search button */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                        <ArrowUpDown className="h-4 w-4 text-slate-400 shrink-0" />
                        <Select value={sortOrder} onValueChange={setSortOrder}>
                            <SelectTrigger className="h-9 rounded-xl border-slate-200 text-sm flex-1">
                                <SelectValue placeholder="Sort by..." />
                            </SelectTrigger>
                            <SelectContent>
                                {SORT_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex justify-end">
                        <Button
                            size="sm"
                            onClick={handleSearch}
                            className="h-9 rounded-xl bg-primary hover:bg-[#002b66] font-bold px-6 shadow-sm w-full sm:w-auto"
                        >
                            Search
                        </Button>
                    </div>
                </div>
            </Card>

            <div className="flex justify-start px-1">
                <Button
                    size="sm"
                    onClick={() => setShowDownloadModal(true)}
                    className="h-9 rounded-xl bg-[#001B44] hover:bg-[#002a6e] text-white font-bold text-xs px-4 gap-1.5 shadow-sm"
                >
                    <FileDown className="w-4 h-4" />
                    Download
                </Button>
            </div>

            {loading ? (
                <div className="flex flex-col gap-3">
                    {[1, 2, 3].map((i) => (
                        <Card key={i} className="h-16 animate-pulse rounded-2xl border border-slate-100" />
                    ))}
                </div>
            ) : sortedApplications.length === 0 ? (
                <Card className="border border-slate-100 shadow-sm rounded-2xl bg-white p-10 flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-bold text-slate-700">No applications found</p>
                    <p className="text-xs text-slate-400">Applications you create will appear here.</p>
                </Card>
            ) : (
                <>
                    <div className="flex flex-col gap-3 md:hidden">
                        {sortedApplications.map((app) => (
                            <Card
                                key={app.application_number}
                                className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4 space-y-2 cursor-pointer hover:border-primary/20 transition-colors"
                                onClick={() => navigate(`/partner/applications/${app.application_number}`)}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md truncate">
                                        {app.application_number}
                                    </span>
                                    <StageBadge stage={app.stage} />
                                </div>
                                <p className="text-sm font-semibold text-slate-800">{app.applicant_name}</p>
                                <p className="text-xs text-slate-500">{app.applied_program_name}</p>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <span>{app.applied_term_name}</span>
                                    {app.status && <><span>·</span><span>{app.status}</span></>}
                                </div>
                            </Card>
                        ))}
                    </div>

                    <Card className="hidden md:block border border-slate-100 shadow-sm rounded-2xl overflow-hidden bg-white">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/50 border-b border-slate-100">
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Application Number</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Applicant Name</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden lg:table-cell whitespace-nowrap">Applied Program</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden lg:table-cell whitespace-nowrap">Stage</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden xl:table-cell whitespace-nowrap">Applied Term</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Application Status</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right whitespace-nowrap">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {sortedApplications.map((app) => (
                                    <tr
                                        key={app.application_number}
                                        className="group hover:bg-slate-50/30 transition-colors cursor-pointer"
                                        onClick={() => app.stage === "Signed Up" && app.member_id
                                            ? navigate(`/partner/students/${app.member_id}/create-application?id=${app.application_number}`)
                                            : navigate(`/partner/applications/${app.application_number}`)
                                        }
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 group-hover:bg-primary/5 transition-colors">
                                                    <GraduationCap className="h-4 w-4 text-slate-400 group-hover:text-primary" />
                                                </div>
                                                <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md">
                                                    {app.application_number}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm font-medium text-slate-700">{app.applicant_name}</span>
                                        </td>
                                        <td className="px-6 py-4 hidden lg:table-cell">
                                            <span className="text-sm text-slate-600">{app.applied_program_name || "—"}</span>
                                        </td>
                                        <td className="px-6 py-4 hidden lg:table-cell">
                                            <StageBadge stage={app.stage} />
                                        </td>
                                        <td className="px-6 py-4 hidden xl:table-cell">
                                            <span className="text-sm text-slate-500">{app.applied_term_name || "—"}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-slate-500">{app.status || "—"}</span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            {app.stage === "Signed Up" && app.member_id ? (
                                                <Button
                                                    variant="default"
                                                    size="sm"
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/partner/students/${app.member_id}/create-application?id=${app.application_number}`); }}
                                                    className="h-8 font-bold text-xs rounded-lg px-3 bg-primary hover:bg-primary/90 text-white"
                                                >
                                                    Complete
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/partner/applications/${app.application_number}`); }}
                                                    className="h-8 font-bold text-xs rounded-lg px-3 border-slate-200 text-slate-600 hover:text-primary"
                                                >
                                                    View
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </>
            )}
        </div>
    );
}
